import type { Dispatch, FC, SetStateAction } from 'react'

import { Select, Input, Checkbox, NotificationBubble, StyledText, Icon } from '@skytech/ui'
import { Button } from '@skytech/ui/Button'
import { toFirstUpperCase } from '@skytech/utils'
import { useQuery } from '@tanstack/react-query'
import CellphoneIcon from 'mdi-react/CellphoneIcon'
import CellphoneKeyIcon from 'mdi-react/CellphoneKeyIcon'
import FinanceIcon from 'mdi-react/FinanceIcon'
import PlusIcon from 'mdi-react/PlusIcon'
import { useNavigate } from 'react-router-dom'
import { twJoin } from 'tailwind-merge'

import { useAppContext } from '@/app/index.context'
import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import { CustomerSelect } from '@/components/CustomerSelect'
import { useIsAdmin, useIsThreeStepIT, useLeasedFeatureAccess } from '@/hooks/access'
import { useSearchParamsByKey } from '@/hooks/searchParams'
import useUser from '@/hooks/user'
import type { Asset } from '@/modules/admin/resources'
import adminResources from '@/modules/admin/resources'
import { APP_ROUTES } from '@/util/app-routes'

import queries from '../queries'
import type { AssetStatusOriginal, UserTypeOriginal } from '../resources/assets/types'

import { ActionsDropdown } from './AssetLeased/ActionsDropdown'
import { useLeasedAssets } from './AssetsLeased.context'
import { leaseAssetTopBgStyleByWhiteLabel } from './helpers'

type AssetTopProps = {
  isAnyAssetsLeased: boolean
  searchFilter: string
  isLoading: boolean
  isSendSMSAvailable: boolean
  setSearchFilter: (search: string) => void
  setIsStatsPopupOpen: Dispatch<SetStateAction<boolean>>
  filterCustomer: Asset[]
  uniqByUserTypes: string[]
  uniqByAssetStatuses: string[]
}

export const AssetTop: FC<AssetTopProps> = ({
  isAnyAssetsLeased,
  searchFilter,
  isLoading,
  isSendSMSAvailable,
  setSearchFilter,
  setIsStatsPopupOpen,
  filterCustomer,
  uniqByUserTypes,
  uniqByAssetStatuses,
}) => {
  const isAdmin = useIsAdmin()
  const navigate = useNavigate()
  const translate = useTranslation()
  const { selectedList, clearSelectedList, handleActionChange } = useLeasedAssets()
  const user = useUser()
  if (!user) throw new Error('No user found for Assets!')

  const { whiteLabel } = useAppContext()
  const isThreeStepIT = useIsThreeStepIT()
  const [queryAssetOwnership, setQueryAssetOwnership] = useSearchParamsByKey('assetOwnership')
  const [queryLeaseReturn, setQueryLeaseReturn] = useSearchParamsByKey('leaseReturn')
  const [queryUserType, setQueryUserType] = useSearchParamsByKey('userType')
  const [queryAssetStatus, setQueryAssetStatus] = useSearchParamsByKey('assetStatus')
  const [queryAllAssets, setQueryAllAssets] = useSearchParamsByKey('allAssets')
  const [queryAssetAge] = useSearchParamsByKey('assetAge')
  const [queryAssetType] = useSearchParamsByKey('assetType')

  const showLeasedFeatures = queryAssetOwnership === 'LEASED' && useLeasedFeatureAccess(user.role)

  const { data: assetTypes } = useQuery(queries.adminAssetTypes(), adminResources.getAssetTypes)

  const statusOptions = [
    { value: 'all', label: translate('All asset statuses') },
    ...(assetTypes?.statuses
      .filter(status => uniqByAssetStatuses.includes(status.status))
      .map(({ status }: { id: number; status: AssetStatusOriginal; description: string }) => ({
        value: status,
        label: toFirstUpperCase(translate(`asset_status/${status}`)),
      })) || []),
  ]

  const userTypeOptions = [
    { value: 'all', label: translate('All user types') },
    ...(assetTypes?.userTypes
      .filter(type => uniqByUserTypes.includes(type.type))
      .map(({ type }: { id: number; type: UserTypeOriginal; description: string }) => ({
        value: type,
        label: translate(`asset_user_type/${type}`),
      })) || []),
  ]

  function handleSelectOwnedAssetOwnership() {
    setQueryAssetOwnership(queryAssetOwnership === 'OWNED' ? '' : 'OWNED')
    setQueryLeaseReturn('')
    clearSelectedList()
  }

  function handleSelectLeasedAssetOwnership() {
    setQueryAssetOwnership(queryAssetOwnership === 'LEASED' ? '' : 'LEASED')
    setQueryLeaseReturn('')
    clearSelectedList()
  }

  function handleSelectLeaseReturn(daysAhead: string) {
    setQueryLeaseReturn(daysAhead === queryLeaseReturn ? ' ' : daysAhead)
    setQueryAssetOwnership('LEASED')
  }

  const onActionChange = (value: string | number) => {
    handleActionChange(value)
  }

  return (
    <>
      {/* Top */}
      <div className="flex flex-col gap-[0.7rem] md:flex-row md:items-center">
        {isAdmin && (
          <Button
            className="mx-0 my-0.5"
            Icon={<PlusIcon />}
            onClick={() => navigate(APP_ROUTES.HARDWARE_ASSETS_NEW)}
          >
            <Translation id="New asset" />
          </Button>
        )}
        <div className="flex flex-row items-center gap-[0.7rem] max-[500px]:flex-col">
          <Button
            className={twJoin(
              'mx-0 my-0.5',
              queryAssetOwnership !== 'OWNED' && 'focus:bg-[#eee] focus:shadow-none',
              isThreeStepIT &&
                (queryAssetOwnership === 'OWNED'
                  ? 'bg-threeStepIT-seaMistPrimary text-threeStepIT-forestGreen border-none'
                  : 'hover:bg-threeStepIT-seaMistPrimary/50 border-none bg-[#eee]')
            )}
            selected={queryAssetOwnership === 'OWNED'}
            onClick={handleSelectOwnedAssetOwnership}
          >
            <>
              <Icon
                className={twJoin(queryAssetOwnership !== 'OWNED' && 'text-black')}
                isWhiteLabeled={queryAssetOwnership === 'OWNED'}
              >
                <CellphoneKeyIcon
                  className={twJoin(
                    'ml-0 mr-1 w-[1.4rem]',
                    isThreeStepIT &&
                      queryAssetOwnership === 'OWNED' &&
                      '!fill-threeStepIT-forestGreen'
                  )}
                />
              </Icon>
              <StyledText
                colorMode={queryAssetOwnership === 'OWNED' ? undefined : 'black'}
                className={twJoin(
                  isThreeStepIT &&
                    queryAssetOwnership === 'OWNED' &&
                    '!text-threeStepIT-forestGreen'
                )}
              >
                <Translation id="Owned" />
              </StyledText>
            </>
          </Button>

          <span
            className={twJoin(
              isThreeStepIT ? 'rounded-xl' : 'rounded-lg',
              queryAssetOwnership === 'LEASED'
                ? leaseAssetTopBgStyleByWhiteLabel[whiteLabel] ||
                    leaseAssetTopBgStyleByWhiteLabel.skytech
                : 'bg-transparent'
            )}
          >
            <span
              className={twJoin(
                'flex flex-row items-center gap-[0.7rem] max-[500px]:flex-col',
                queryAssetOwnership === 'LEASED' ? 'mx-1.5 my-1' : 'my-1'
              )}
            >
              <Button
                className={twJoin(
                  'mx-0 my-0.5',
                  queryAssetOwnership !== 'LEASED' && 'focus:bg-[#eee] focus:shadow-none',
                  isThreeStepIT &&
                    (queryAssetOwnership === 'LEASED'
                      ? 'bg-threeStepIT-seaMistPrimary text-threeStepIT-forestGreen border-none'
                      : 'hover:bg-threeStepIT-seaMistPrimary/50 border-none bg-[#eee]')
                )}
                selected={queryAssetOwnership === 'LEASED'}
                disabled={queryAssetOwnership !== 'LEASED' && !isAnyAssetsLeased}
                onClick={handleSelectLeasedAssetOwnership}
              >
                <>
                  <Icon
                    className={twJoin(queryAssetOwnership !== 'LEASED' && 'text-black')}
                    isWhiteLabeled={queryAssetOwnership === 'LEASED' && !isThreeStepIT}
                  >
                    <CellphoneIcon
                      className={twJoin(
                        'ml-0 mr-1 w-[1.4rem]',
                        isThreeStepIT &&
                          queryAssetOwnership === 'LEASED' &&
                          '!fill-threeStepIT-forestGreen'
                      )}
                    />
                  </Icon>
                  <StyledText
                    colorMode={queryAssetOwnership === 'LEASED' ? undefined : 'black'}
                    className={twJoin(
                      isThreeStepIT &&
                        queryAssetOwnership === 'LEASED' &&
                        '!text-threeStepIT-forestGreen'
                    )}
                  >
                    <Translation id="Leased" />
                  </StyledText>
                </>
              </Button>

              {queryAssetOwnership === 'LEASED' && (
                <>
                  <Select
                    id="lease-return"
                    className={twJoin('min-w-44')}
                    value={queryLeaseReturn || ''}
                    options={[
                      { value: '', label: translate('Return date') },
                      {
                        value: '30',
                        label: `${translate('Within {count} days', { count: '30' })}`,
                      },
                      {
                        value: '60',
                        label: `${translate('Within {count} days', { count: '60' })}`,
                      },
                      {
                        value: '90',
                        label: `${translate('Within {count} days', { count: '90' })}`,
                      },
                    ]}
                    onChange={option => {
                      clearSelectedList()
                      handleSelectLeaseReturn(option?.value || '')
                    }}
                  />

                  {showLeasedFeatures && (
                    <ActionsDropdown
                      isDisabled={selectedList.length === 0}
                      isSendSMSAvailable={isSendSMSAvailable}
                      onChange={onActionChange}
                    />
                  )}
                </>
              )}
            </span>
          </span>
        </div>
      </div>

      {/* Bottom */}
      <div className="table-actions">
        <div className="search">
          <Input
            id="admin-asset-search"
            type="text"
            value={searchFilter}
            placeholder={translate('Search')}
            onChange={e => setSearchFilter(e.target.value)}
          />
        </div>

        {/* <div className="flex w-full flex-wrap items-center justify-between gap-[0.7rem] max-[500px]:justify-start max-[500px]:gap-y-[0.7rem] xl:flex-nowrap"> */}
        <div className="flex flex-wrap items-center gap-[0.7rem] xl:flex-nowrap">
          <CustomerSelect filterData={filterCustomer} isLoading={isLoading} />

          <Select
            id="user-type"
            className="min-w-44"
            value={queryUserType || 'all'}
            options={userTypeOptions}
            isLoading={isLoading}
            onChange={option => setQueryUserType(option?.value || '')}
          />
          <Select
            id="asset-status"
            className="min-w-44"
            value={queryAssetStatus || 'all'}
            options={statusOptions}
            isLoading={isLoading}
            onChange={option => setQueryAssetStatus(option?.value || '')}
          />
          <Checkbox
            id="show-hidden-input"
            marginTop={0}
            label={<Translation id="Show hidden" />}
            checked={queryAllAssets ? JSON.parse(queryAllAssets) : false}
            onChange={() => {
              const updatedShowHidden = queryAllAssets
                ? JSON.parse(queryAllAssets)
                  ? 'false'
                  : 'true'
                : 'true'
              setQueryAllAssets(updatedShowHidden)
            }}
          />

          <Button
            className="m-0"
            Icon={<FinanceIcon />}
            onClick={() => setIsStatsPopupOpen((prev: boolean) => !prev)}
          >
            <Translation id="Stats" />
            {queryAssetAge && queryAssetType ? (
              <NotificationBubble className="sticky">2</NotificationBubble>
            ) : queryAssetAge || queryAssetType ? (
              <NotificationBubble className="sticky">1</NotificationBubble>
            ) : null}
          </Button>
        </div>
        {/* </div> */}
      </div>
    </>
  )
}
